import moment from "moment-timezone";
import { calculatePeriodOffsets } from "@/lib/performance_date_filters.js";
import { getQueryVariables } from "@/lib/url.js";

import CONSTANTS from "@/Constants.js";
import { capitalize } from "@/mixins";

export const doughnutTextPlugin = {
  beforeDraw: function (chart) {
    const FONT_SIZE_DENOM = 65;
    const TEXT_Y_OFFSET = 3;
    const width = chart.chart.width;
    const height = chart.chart.height;
    const ctx = chart.chart.ctx;
    ctx.restore();
    const fontSize = (height / FONT_SIZE_DENOM).toFixed(2);
    ctx.font = fontSize + "em Inter";
    ctx.textBaseline = "middle";
    const text = chart.config.options.elements.center.text;
    const textX = Math.round((width - ctx.measureText(text).width) / 2);
    const textY = height / 2 + TEXT_Y_OFFSET;
    ctx.fillText(text, textX, textY);
    ctx.save();
  }
};

const getTimeSeriesLabels = (data, period) => {
  return data.map((point) => {
    if (!point.base) {
      return "";
    }
    const date = moment(point.base.date);
    const label = [formatDateLabel(point.base, period)];
    switch (period) {
      case "year":
      case "quarter": {
        // Show quarter label
        const quarterStart = date.clone().startOf("quarter");
        if (date.isSame(quarterStart)) {
          label.push(`Q${date.quarter()}`);
        }
        break;
      }
      default: {
        // Show Monday label
        if (date.day() === 1) {
          label.push("Mon");
        }
      }
    }
    return point.base ? label : "";
  });
};

export const formatDateLabel = (point, period, cumulative = true) => {
  if (period === "year") {
    return formatYearLabel(point);
  }
  if (period === "quarter") {
    return formatQuarterLabel(point);
  }

  return cumulative
    ? moment(point.date).format(CONSTANTS.DATE_FORMAT_SHORT)
    : point.date;
};

const formatYearLabel = (point) => {
  const month = moment(point.date).format("MMM");
  const year = moment(point.date).format("YY");
  return `${month}'${year}`;
};

const formatQuarterLabel = (point) => {
  const start = moment(point.date).format("D");
  const end = moment(point.date_end).format("DD");
  return `${moment(point.date).format("MMM")} ${start}-${end}`;
};

export const formatComparisonSeriesData = (
  data,
  baseLabel,
  comparisonLabel,
  key = "cumulative",
  period
) => {
  const chartData = data.reduce(
    (data, point) => {
      data.base.data.push(point.base ? point.base[key] : null);
      data.base.values.push(point.base ? point.base.value : null);
      data.comparison.data.push(
        point.comparison ? point.comparison[key] : null
      );
      data.comparison.values.push(
        point.comparison ? point.comparison.value : null
      );
      return data;
    },
    {
      base: {
        label: baseLabel,
        legendLabel: baseLabel,
        data: [],
        values: [],
        key: "base"
      },
      comparison: {
        label: comparisonLabel,
        legendLabel: comparisonLabel,
        data: [],
        values: [],
        key: "comparison"
      }
    }
  );

  return {
    chartData: [chartData.base, chartData.comparison],
    labels: getTimeSeriesLabels(data, period)
  };
};

export const timeSeriesChartClick = (data, e) => {
  const key = e.dataset.key;
  const point = data[e.pointIndex][key];
  if (point && point.detail_report_url) {
    if (e.e.metaKey) {
      window.open(point.detail_report_url);
    } else {
      window.location = point.detail_report_url;
    }
  }
};

export const getComparisonLegend = () => {
  const params = getQueryVariables();
  const period = capitalize(params.period || "");
  const offsetLabel = getQueryOffsetLabel(params.period, params.offset);
  const baseLabel = capitalize(offsetLabel);
  const comparisonLabel =
    params.comparisonPeriod === "samePeriod"
      ? `Prior ${period}`
      : `Same ${period} Last Year`;
  return {
    base: baseLabel,
    comparison: comparisonLabel
  };
};

const getQueryOffsetLabel = (period, queryOffset) => {
  const offsets = calculatePeriodOffsets(period);
  const offset = offsets.find((offset) => offset.value === queryOffset);
  return offset?.label || "";
};

// Copied from Chart.js scale, since it is not exported there.
const getLineValue = (scale, index, offsetGridLines) => {
  let lineValue = scale.getPixelForTick(index);

  if (offsetGridLines) {
    if (index === 0) {
      lineValue -= (scale.getPixelForTick(1) - lineValue) / 2;
    } else {
      lineValue -= (lineValue - scale.getPixelForTick(index - 1)) / 2;
    }
  }
  return lineValue;
};

// Custom plugin for setting gridline background
export const gridLineBackgroundPlugin = {
  id: "gridBgColor",

  beforeDraw: function (chartInstance) {
    // This is what the plugin does.
    //
    //		for (scales) {
    //			for (gridLines) {
    //				if (isarray(backgroundColor)) {
    //					draw the grindline background
    //				} else { if (isString(backgroundColor)) {
    //								draw the background for the whole axis, thus for the chartArea
    //						 }
    //				}
    //			}
    //		}

    const ctx = chartInstance.chart.ctx;
    const chartArea = chartInstance.chartArea;

    let scale;
    let si;
    let ti;
    let bgCol = [],
      bgColRpt;
    let x1, y1, x2, y2;
    let axisWidth;
    let isHorizontal;
    let scaleType;
    let gridLines;
    let lineWidth;

    for (si in chartInstance.scales) {
      scale = chartInstance.scales[si];
      axisWidth = scale.options.gridLines.lineWidth;
      isHorizontal = scale.isHorizontal();
      scaleType = scale.options.type;
      gridLines = chartInstance.scales[si].options.gridLines;
      lineWidth = gridLines.lineWidth;
      bgCol =
        scale.options.gridLines.backgroundColor || "rgba(127,127,127,0.0)";
      bgCol =
        bgCol == undefined
          ? chartInstance.chart.options.plugins.gridBgColor.backgroundColor ||
            "rgba(127,127,127,0.0)"
          : bgCol;
      bgColRpt = scale.options.gridLines.backgroundColorRepeat || false;
      bgColRpt =
        bgColRpt == undefined
          ? chartInstance.chart.options.plugins.gridBgColor
              .backgroundColorRepeat || false
          : bgColRpt;

      if (!scale.options.display || bgCol == undefined) {
        // No background coloring for this scale; next scale.
        continue;
      }

      if (typeof bgCol === "string") {
        // One color for the complete scale background.
        // One rectangle for the chart area.
        ctx.fillStyle = bgCol;
        ctx.fillRect(
          chartArea.left,
          chartArea.bottom,
          chartArea.right - chartArea.left,
          chartArea.top - chartArea.bottom
        );
        continue; // next scale
      }

      if (bgCol.constructor === Array) {
        // Color the individual gridline backgrounds.
        // Algorithm in essence:
        // Loop through the gridLines (ticks),
        //	 If backgroundColor is different from previous,
        //		 draw the background rectangle and
        //		 save the new coordinates as the start of the next rectangle

        // A simpler algorithm would be to draw each gridLine's background individually,
        // but I got overlaps and gaps (of 1 pixel width), so that was too ugly.
        // Plus, this one is more efficient, as it draws less background rectangles.

        const prevTick = {
          bgCol: "",
          x1: 0,
          y1: 0,
          x2: 0,
          y2: 0
        };
        var curBgCol;
        const numToDo = scale.ticks.length - (scaleType != "category" ? 1 : 0);

        for (ti = 0; ti <= numToDo; ti++) {
          // Note: this does one extra tick!

          ti = Number(ti);

          if (ti == numToDo) {
            // This is the last gridline, make sure we draw it.
            // Also, there probably is no more bgCol[ti]
            curBgCol = "differentFromPrevious";
          } else {
            // Repeat the bgColors if necessary.
            if (bgColRpt) {
              curBgCol = bgCol[ti % bgCol.length];
            } else {
              curBgCol = bgCol[ti];
            }
          }

          if (curBgCol != prevTick.bgCol) {
            // Some code copied from Chart.js scale.
            if (isHorizontal) {
              // Vertical grid line / grid background
              let xLineValue = getLineValue(
                scale,
                ti,
                gridLines.offsetGridLines && scale.ticks.length > 1
              );
              xLineValue += lineWidth % 2 === 0 ? 0 : 0.5;
              x1 = x2 = xLineValue;
              y1 = chartArea.top;
              y2 = chartArea.bottom + axisWidth;
            } else {
              // Horizontal grid line
              let yLineValue = getLineValue(
                scale,
                ti,
                gridLines.offsetGridLines && scale.ticks.length > 1
              );
              yLineValue += lineWidth % 2 === 0 ? 0 : 0.5;
              x1 = chartArea.left;
              x2 = chartArea.right + axisWidth;
              y1 = y2 = yLineValue;
            }

            if (gridLines.drawOnChartArea && prevTick.bgCol != "") {
              ctx.save();
              ctx.fillStyle = prevTick.bgCol;

              if (isHorizontal) {
                // Horizontal scale, vertical grid line / grid background
                ctx.fillRect(
                  prevTick.x1,
                  prevTick.y1,
                  Math.abs(x2 - prevTick.x1),
                  Math.abs(y2 - prevTick.y1)
                );
              } else {
                // Horizontal grid line
                ctx.fillRect(
                  prevTick.x1,
                  prevTick.y1,
                  Math.abs(x2 - prevTick.x1),
                  Math.abs(y2 - prevTick.y1)
                );
              }
            } // gl.drawonChartArea
            ctx.restore();

            // Save the new coordinates
            prevTick.bgCol = curBgCol;
            prevTick.x1 = x1;
            prevTick.y1 = y1;
            prevTick.x2 = x2;
            prevTick.y2 = y2;
          } // bgCol != bgCol
        } //for ti in ticks

        continue;
      } // if bgCol is of type array

      // If we get here, that's an error!
      console.log(
        "chartjs-plugin-gridline-background.js: Error, unknown type of backgroundColor (not String or Array): " +
          bgCol
      );
    } // for si
  } // beforeDraw: function
};

export const colorPalette = ["#0F3B95", "#9CA4AA"];

const shadow = {
  color: "rgba(0, 0, 0, 0.2)",
  offsetX: 0,
  offsetY: 3,
  blur: 2
};

export const typeDefaults = {
  datasets: {
    line: {
      fill: false,
      pointRadius: 2,
      pointHitRadius: 2,
      tension: 0,
      borderWidth: 2,
      shadowOffsetX: shadow.offsetX,
      shadowOffsetY: shadow.offsetY,
      shadowBlur: shadow.blur,
      shadowColor: shadow.color,
      pointShadowOffsetX: shadow.offsetX,
      pointShadowOffsetY: shadow.offsetY,
      pointShadowBlur: shadow.blur,
      pointShadowColor: shadow.color
    },
    doughnut: {
      borderColor: "#FFF",
      backgroundColor: colorPalette
    },
    pie: {
      borderColor: "#FFF",
      backgroundColor: colorPalette
    }
  },
  options: {
    doughnut: {
      cutoutPercentage: 65,
      scales: null,
      rotation: 2 * Math.PI
    },
    line: {}
  }
};
